import { Injectable, OnDestroy } from '@angular/core';
import { BackendApiService } from './backend-api';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import {
  AdditionalNotes,
  ReminderPrompter,
} from 'src/app/shared/models/database-models';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';

interface NoticeStatusData<T> {
  error: string;
  isLoading: boolean;
  data: T | null;
}

export enum NoticeName {
  GewcaMaterialNote = 'GewcaMaterialNote',
  AOPRReminder = 'AOPRReminder',
  PRReminder = 'PRReminder',
  ITProductPolicyCat1 = 'ITProductPolicyCat1',
  ITProductPolicyCat2 = 'ITProductPolicyCat2',
  ITProductPolicyAO_SD = 'ITProductPolicyAO_SD',
  ITProductPolicyAO_SDCDO = 'ITProductPolicyAO_SDCDO',
  AdvisoryConflictInterestITPolicy = 'AdvisoryConflictInterestITPolicy',
}

const NOTICE_CODES = Object.freeze({
  [NoticeName.GewcaMaterialNote]: 'GEWCAMaterialNote',
  [NoticeName.AOPRReminder]: 'Info_SupportingDocument_AO_PR',
  [NoticeName.PRReminder]: 'Info_SupportingDocument_PR',
  [NoticeName.ITProductPolicyCat1]: 'RestrictedITProductPolicyCat1',
  [NoticeName.ITProductPolicyCat2]: 'RestrictedITProductPolicyCat2',
  [NoticeName.ITProductPolicyAO_SD]: 'RestrictedITProductPolicyAO_SD',
  [NoticeName.ITProductPolicyAO_SDCDO]: 'RestrictedITProductPolicyAO_SDCDO',
});

interface Notices {
  [NoticeName.GewcaMaterialNote]: NoticeStatusData<AdditionalNotes>;
  [NoticeName.AOPRReminder]: NoticeStatusData<ReminderPrompter>;
  [NoticeName.PRReminder]: NoticeStatusData<ReminderPrompter>;
  [NoticeName.ITProductPolicyCat1]: NoticeStatusData<AdditionalNotes[]>;
  [NoticeName.ITProductPolicyCat2]: NoticeStatusData<AdditionalNotes[]>;
  [NoticeName.ITProductPolicyAO_SD]: NoticeStatusData<AdditionalNotes>;
  [NoticeName.ITProductPolicyAO_SDCDO]: NoticeStatusData<AdditionalNotes>;
}

const INITIAL_NOTICES: Notices = {
  [NoticeName.GewcaMaterialNote]: {
    error: '',
    isLoading: false,
    data: null,
  },
  [NoticeName.AOPRReminder]: {
    error: '',
    isLoading: false,
    data: null,
  },
  [NoticeName.PRReminder]: {
    error: '',
    isLoading: false,
    data: null,
  },
  [NoticeName.ITProductPolicyCat1]: {
    error: '',
    isLoading: false,
    data: null,
  },
  [NoticeName.ITProductPolicyCat2]: {
    error: '',
    isLoading: false,
    data: null,
  },
  [NoticeName.ITProductPolicyAO_SD]: {
    error: '',
    isLoading: false,
    data: null,
  },
  [NoticeName.ITProductPolicyAO_SDCDO]: {
    error: '',
    isLoading: false,
    data: null,
  },
};

@Injectable({ providedIn: 'root' })
export class NoticeService implements OnDestroy {
  /* Individual notice observables */
  private fetchedGewcaMaterialNoteNotice = new BehaviorSubject<
    Notices[NoticeName.GewcaMaterialNote]
  >(INITIAL_NOTICES[NoticeName.GewcaMaterialNote]);
  public gewcaMaterialNoteNotice$ =
    this.fetchedGewcaMaterialNoteNotice.asObservable();

  private fetchedITProductPolicyCat1Notice = new BehaviorSubject<
    Notices[NoticeName.ITProductPolicyCat1]
  >(INITIAL_NOTICES[NoticeName.ITProductPolicyCat1]);
  public ITProductPolicyCat1Notice$ =
    this.fetchedITProductPolicyCat1Notice.asObservable();

  private fetchedITProductPolicyCat2Notice = new BehaviorSubject<
    Notices[NoticeName.ITProductPolicyCat2]
  >(INITIAL_NOTICES[NoticeName.ITProductPolicyCat2]);
  public ITProductPolicyCat2Notice$ =
    this.fetchedITProductPolicyCat2Notice.asObservable();

  private fetchedITProductPolicyAOSDNotice = new BehaviorSubject<
    Notices[NoticeName.ITProductPolicyAO_SD]
  >(INITIAL_NOTICES[NoticeName.ITProductPolicyAO_SD]);
  public ITProductPolicyAOSDNotice$ =
    this.fetchedITProductPolicyAOSDNotice.asObservable();

  private fetchedITProductPolicyAOSDCDONotice = new BehaviorSubject<
    Notices[NoticeName.ITProductPolicyAO_SDCDO]
  >(INITIAL_NOTICES[NoticeName.ITProductPolicyAO_SDCDO]);
  public ITProductPolicyAOSDCDONotice$ =
    this.fetchedITProductPolicyAOSDCDONotice.asObservable();

  private fetchedAOPRReminderNotice = new BehaviorSubject<
    Notices[NoticeName.AOPRReminder]
  >(INITIAL_NOTICES[NoticeName.AOPRReminder]);
  public aoPrReminderNotice$ = this.fetchedAOPRReminderNotice.asObservable();

  private fetchedPRReminderNotice = new BehaviorSubject<
    Notices[NoticeName.PRReminder]
  >(INITIAL_NOTICES[NoticeName.PRReminder]);
  public prReminderNotice$ = this.fetchedPRReminderNotice.asObservable();

  /* Combined notices observable */
  private fetchedNotices = new BehaviorSubject<Notices>(INITIAL_NOTICES);
  public notices$ = this.fetchedNotices.asObservable();

  /* Active notice observable */
  private activeNotice = new BehaviorSubject<NoticeName | null>(null);
  public activeNotice$ = this.activeNotice.asObservable();

  /* Cleanup and temp helpers */
  private combineSubscription: Subscription;
  private allNoticeSubjects: BehaviorSubject<NoticeStatusData<any>>[] = [];
  private apiSubscriptions: Subscription[] = [];

  constructor(private backendApi: BackendApiService, private router: Router) {
    // On route change, clear active notice, and by extension, close notice dialog
    // router.events.subscribe((event) => {
    //   if (event instanceof NavigationEnd) {
    //     console.log('NOTICE SERVICE ended navigation');
    //     this.clearActiveNotice();
    //   }
    // });

    this.combineSubscription = combineLatest(
      [
        this.gewcaMaterialNoteNotice$,
        this.aoPrReminderNotice$,
        this.prReminderNotice$,
        this.ITProductPolicyCat1Notice$,
        this.ITProductPolicyCat2Notice$,
        this.ITProductPolicyAOSDNotice$,
        this.ITProductPolicyAOSDCDONotice$,
      ],
      (
        gewcaMaterialNoteNotice,
        aoPrReminderNotice,
        prReminderNotice,
        ITProductPolicyCat1Notice,
        ITProductPolicyCat2Notice,
        ITProductPolicyAOSDNotice,
        ITProductPolicyAOSDCDONotice
      ) => {
        // console.log(
        //   'combined!',
        //   gewcaMaterialNoteNotice,
        //   aoPrReminderNotice,
        //   prReminderNotice
        // );
        return {
          [NoticeName.GewcaMaterialNote]: gewcaMaterialNoteNotice,
          [NoticeName.AOPRReminder]: aoPrReminderNotice,
          [NoticeName.PRReminder]: prReminderNotice,
          [NoticeName.ITProductPolicyCat1]: ITProductPolicyCat1Notice,
          [NoticeName.ITProductPolicyCat2]: ITProductPolicyCat2Notice,
          [NoticeName.ITProductPolicyAO_SD]: ITProductPolicyAOSDNotice,
          [NoticeName.ITProductPolicyAO_SDCDO]: ITProductPolicyAOSDCDONotice,
        };
      }
    ).subscribe(this.fetchedNotices);

    this.allNoticeSubjects.push(
      this.fetchedGewcaMaterialNoteNotice,
      this.fetchedAOPRReminderNotice,
      this.fetchedPRReminderNotice,
      this.fetchedITProductPolicyCat1Notice,
      this.fetchedITProductPolicyCat2Notice,
      this.fetchedITProductPolicyAOSDNotice,
      this.fetchedITProductPolicyAOSDCDONotice,
    );

    this.onFetchNotices();
  }

  public setActiveNotice(noticeName: NoticeName): void {
    // console.log('set active notice = ' + noticeName);
    this.activeNotice.next(noticeName);
  }

  public clearActiveNotice(): void {
    if (this.activeNotice.value) {
      this.activeNotice.next(null);
    }
  }

  public onFetchNotices(): void {
    for (const behaviorSubject of this.allNoticeSubjects) {
      behaviorSubject.next({
        ...behaviorSubject.value,
        error: '',
        isLoading: true,
      });
    }

    const additionalNotesSubscription = this.backendApi
      .getAdditionalNotes([
        NOTICE_CODES[NoticeName.GewcaMaterialNote],
        NOTICE_CODES[NoticeName.ITProductPolicyCat1],
        NOTICE_CODES[NoticeName.ITProductPolicyCat2],
        NOTICE_CODES[NoticeName.ITProductPolicyAO_SD],
        NOTICE_CODES[NoticeName.ITProductPolicyAO_SDCDO],
      ])
      .subscribe({
        next: (response: AdditionalNotes[]): void => {
          const gewcaMaterialNote = response?.find(
            (item) => item.Code === NOTICE_CODES[NoticeName.GewcaMaterialNote]
          );
          if (gewcaMaterialNote) {
            this.fetchedGewcaMaterialNoteNotice.next({
              error: '',
              isLoading: false,
              data: gewcaMaterialNote,
            });
          } else {
            this.fetchedGewcaMaterialNoteNotice.next({
              error: 'Failed to retrieve information.',
              isLoading: false,
              data: null,
            });
          }

          const ITProductPolicyCat1 = response?.filter(
            (item) => item.Code === NOTICE_CODES[NoticeName.ITProductPolicyCat1]
          );
          if (ITProductPolicyCat1) {
            this.fetchedITProductPolicyCat1Notice.next({
              error: '',
              isLoading: false,
              data: ITProductPolicyCat1,
            });
          } else {
            this.fetchedITProductPolicyCat1Notice.next({
              error: 'Failed to retrieve information.',
              isLoading: false,
              data: null,
            });
          }

          const ITProductPolicyCat2 = response?.filter(
            (item) => item.Code === NOTICE_CODES[NoticeName.ITProductPolicyCat2]
          );
          if (ITProductPolicyCat2) {
            this.fetchedITProductPolicyCat2Notice.next({
              error: '',
              isLoading: false,
              data: ITProductPolicyCat2,
            });
          } else {
            this.fetchedITProductPolicyCat2Notice.next({
              error: 'Failed to retrieve information.',
              isLoading: false,
              data: null,
            });
          }

          const ITProductPolicyAOSD = response?.find(
            (item) => item.Code === NOTICE_CODES[NoticeName.ITProductPolicyAO_SD]
          );
          if (ITProductPolicyAOSD) {
            this.fetchedITProductPolicyAOSDNotice.next({
              error: '',
              isLoading: false,
              data: ITProductPolicyAOSD,
            });
          } else {
            this.fetchedITProductPolicyAOSDNotice.next({
              error: 'Failed to retrieve information.',
              isLoading: false,
              data: null,
            });
          }

          const ITProductPolicyAOSDCDO = response?.find(
            (item) => item.Code === NOTICE_CODES[NoticeName.ITProductPolicyAO_SDCDO]
          );
          if (ITProductPolicyAOSDCDO) {
            this.fetchedITProductPolicyAOSDCDONotice.next({
              error: '',
              isLoading: false,
              data: ITProductPolicyAOSDCDO,
            });
          } else {
            this.fetchedITProductPolicyAOSDCDONotice.next({
              error: 'Failed to retrieve information.',
              isLoading: false,
              data: null,
            });
          }
        },
        error: (errMsg): void => {
          this.fetchedGewcaMaterialNoteNotice.next({
            error: errMsg ?? 'Failed to retrieve information.',
            isLoading: false,
            data: null,
          });

          this.fetchedITProductPolicyCat1Notice.next({
            error: errMsg ?? 'Failed to retrieve information.',
            isLoading: false,
            data: null,
          });

          this.fetchedITProductPolicyCat2Notice.next({
            error: errMsg ?? 'Failed to retrieve information.',
            isLoading: false,
            data: null,
          });

          this.fetchedITProductPolicyAOSDNotice.next({
            error: errMsg ?? 'Failed to retrieve information.',
            isLoading: false,
            data: null,
          });

          this.fetchedITProductPolicyAOSDCDONotice.next({
            error: errMsg ?? 'Failed to retrieve information.',
            isLoading: false,
            data: null,
          });
        },
      });

    const reminderPromptersSubscription = this.backendApi
      .getReminderPrompters([
        NOTICE_CODES[NoticeName.AOPRReminder],
        NOTICE_CODES[NoticeName.PRReminder],
      ])
      .subscribe({
        next: (response: ReminderPrompter[]): void => {
          const aoPrReminder = response?.find(
            (item) => item.Code === NOTICE_CODES[NoticeName.AOPRReminder]
          );
          const prReminder = response?.find(
            (item) => item.Code === NOTICE_CODES[NoticeName.PRReminder]
          );

          if (aoPrReminder) {
            this.fetchedAOPRReminderNotice.next({
              error: '',
              isLoading: false,
              data: aoPrReminder,
            });
          } else {
            this.fetchedAOPRReminderNotice.next({
              error: 'Failed to retrieve information.',
              isLoading: false,
              data: null,
            });
          }

          if (prReminder) {
            this.fetchedPRReminderNotice.next({
              error: '',
              isLoading: false,
              data: prReminder,
            });
          } else {
            this.fetchedPRReminderNotice.next({
              error: 'Failed to retrieve information.',
              isLoading: false,
              data: null,
            });
          }
        },
        error: (errMsg): void => {
          this.fetchedAOPRReminderNotice.next({
            error: errMsg ?? 'Failed to retrieve information.',
            isLoading: false,
            data: null,
          });

          this.fetchedPRReminderNotice.next({
            error: errMsg ?? 'Failed to retrieve information.',
            isLoading: false,
            data: null,
          });
        },
      });

    this.apiSubscriptions.push(
      additionalNotesSubscription,
      reminderPromptersSubscription
    );
  }

  ngOnDestroy(): void {
    for (const subscription of this.apiSubscriptions) {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
    }

    this.combineSubscription.unsubscribe();
  }
}
